import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DisputeResolutionPanel = ({ 
  disputes = [],
  onResolveDispute = () => {},
  onCreateDispute = () => {},
  participants = []
}) => {
  const [isCreatingDispute, setIsCreatingDispute] = useState(false);
  const [newDispute, setNewDispute] = useState({
    type: 'item-allocation',
    description: '',
    affectedParticipants: [],
    proposedSolution: ''
  });

  const disputeTypes = [
    { id: 'item-allocation', label: 'Asignación de artículos', icon: 'ShoppingCart' },
    { id: 'tip-calculation', label: 'Cálculo de propina', icon: 'Calculator' },
    { id: 'tax-distribution', label: 'Distribución de impuestos', icon: 'Receipt' },
    { id: 'payment-amount', label: 'Cantidad de pago', icon: 'CreditCard' }
  ];

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const getDisputeStatusColor = (status) => {
    switch (status) {
      case 'resolved':
        return 'text-success bg-success/10 border-success/20';
      case 'pending':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'escalated':
        return 'text-error bg-error/10 border-error/20';
      default:
        return 'text-muted-foreground bg-muted/10 border-border';
    }
  };

  const getDisputeStatusLabel = (status) => {
    switch (status) {
      case 'resolved':
        return 'Resuelto';
      case 'pending':
        return 'Pendiente';
      case 'escalated':
        return 'Escalado';
      default:
        return 'Nuevo';
    }
  };

  const handleCreateDispute = () => {
    if (newDispute?.description?.trim()) {
      onCreateDispute({
        ...newDispute,
        id: Date.now()?.toString(),
        createdAt: new Date()?.toISOString(),
        status: 'pending'
      });
      
      setNewDispute({
        type: 'item-allocation',
        description: '',
        affectedParticipants: [],
        proposedSolution: ''
      });
      setIsCreatingDispute(false);
    }
  };

  const handleParticipantToggle = (participantId) => {
    setNewDispute(prev => ({
      ...prev,
      affectedParticipants: prev?.affectedParticipants?.includes(participantId)
        ? prev?.affectedParticipants?.filter(id => id !== participantId)
        : [...prev?.affectedParticipants, participantId]
    }));
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Resolución de Disputas</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsCreatingDispute(!isCreatingDispute)}
          iconName="Plus"
          iconPosition="left"
          iconSize={14}
        >
          Nueva Disputa
        </Button>
      </div>
      {/* Create New Dispute Form */}
      {isCreatingDispute && (
        <div className="mb-6 p-4 bg-muted/30 border border-border rounded-lg">
          <h4 className="font-medium text-foreground mb-3">Crear Nueva Disputa</h4>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Tipo de disputa:
              </label>
              <div className="grid grid-cols-2 gap-2">
                {disputeTypes?.map((type) => (
                  <button
                    key={type?.id}
                    onClick={() => setNewDispute(prev => ({ ...prev, type: type?.id }))}
                    className={`p-2 rounded-lg border text-left transition-all duration-200 ${
                      newDispute?.type === type?.id
                        ? 'border-primary bg-primary/5 text-foreground'
                        : 'border-border bg-card hover:border-primary/30 text-foreground'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <Icon name={type?.icon} size={16} />
                      <span className="text-sm">{type?.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <Input
              label="Descripción del problema"
              type="text"
              placeholder="Describe el problema que necesita resolución..."
              value={newDispute?.description}
              onChange={(e) => setNewDispute(prev => ({ ...prev, description: e?.target?.value }))}
            />

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Participantes afectados:
              </label>
              <div className="space-y-2">
                {participants?.map((participant) => (
                  <label key={participant?.id} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newDispute?.affectedParticipants?.includes(participant?.id)}
                      onChange={() => handleParticipantToggle(participant?.id)}
                      className="rounded border-border"
                    />
                    <span className="text-sm text-foreground">{participant?.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <Input
              label="Solución propuesta (opcional)"
              type="text"
              placeholder="Propón una solución para resolver el problema..."
              value={newDispute?.proposedSolution}
              onChange={(e) => setNewDispute(prev => ({ ...prev, proposedSolution: e?.target?.value }))}
            />

            <div className="flex space-x-2">
              <Button
                variant="default"
                onClick={handleCreateDispute}
                iconName="Check"
                iconPosition="left"
                iconSize={14}
              >
                Crear Disputa
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsCreatingDispute(false)}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Existing Disputes */}
      {disputes?.length > 0 ? (
        <div className="space-y-3">
          {disputes?.map((dispute) => (
            <div key={dispute?.id} className="p-4 border border-border rounded-lg">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon 
                      name={disputeTypes?.find(t => t?.id === dispute?.type)?.icon || 'AlertCircle'} 
                      size={16} 
                      className="text-text-secondary" 
                    />
                    <span className="font-medium text-foreground">
                      {disputeTypes?.find(t => t?.id === dispute?.type)?.label || 'Disputa'}
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary mb-2">{dispute?.description}</p>
                  
                  {dispute?.affectedParticipants?.length > 0 && (
                    <div className="flex items-center space-x-1 mb-2">
                      <Icon name="Users" size={14} className="text-text-secondary" />
                      <span className="text-xs text-text-secondary">
                        Afecta a: {dispute?.affectedParticipants?.map(id => 
                          participants?.find(p => p?.id === id)?.name
                        )?.filter(Boolean)?.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
                
                <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getDisputeStatusColor(dispute?.status)}`}>
                  {getDisputeStatusLabel(dispute?.status)}
                </div>
              </div>

              {dispute?.proposedSolution && (
                <div className="mb-3 p-2 bg-primary/5 border border-primary/20 rounded">
                  <span className="text-xs font-medium text-primary">Solución propuesta:</span>
                  <p className="text-sm text-foreground mt-1">{dispute?.proposedSolution}</p>
                </div>
              )}

              {dispute?.status === 'pending' && (
                <div className="flex space-x-2">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => onResolveDispute(dispute?.id, 'resolved')}
                    iconName="Check"
                    iconPosition="left"
                    iconSize={14}
                  >
                    Resolver
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onResolveDispute(dispute?.id, 'escalated')}
                    iconName="AlertTriangle"
                    iconPosition="left"
                    iconSize={14}
                  >
                    Escalar
                  </Button>
                </div>
              )}

              <div className="text-xs text-text-secondary mt-2">
                Creado: {new Date(dispute.createdAt)?.toLocaleString('es-ES')}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <Icon name="CheckCircle" size={32} className="mx-auto text-success mb-2" />
          <p className="text-sm text-text-secondary">No hay disputas activas</p>
          <p className="text-xs text-text-secondary mt-1">
            Todos los pagos están correctamente asignados
          </p>
        </div>
      )}
    </div>
  );
};

export default DisputeResolutionPanel;